import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMantineTheme } from '@mantine/core';
import { IconCurrencyDollar, IconTarget, IconShoppingCart, IconTrendingUp, IconChevronUp, IconChartBar } from '@tabler/icons-react';
import TestsApi from '../../api/TestsApi';
import { STATUS, withAlpha } from '../../utils/statusTokens';
import { visiUserSession, setVisiUserSession } from '../../GeneralFunction';
import './ImpactSummary.css';

// Predictive Winner Detection states that name a leader. Any other state means
// there is no leader yet, so there is nothing to project.
const LEADER_STATES: Record<string, { label: string; color: string }> = {
  TRENDING: { label: 'Trending Leader', color: STATUS.trending },
  PROCEED_WITH_CAUTION: { label: 'Caution Leader', color: STATUS.warning },
  PREDICTED_WINNER: { label: 'Predicted Winner', color: STATUS.good },
};
const CONTROL_ID = 1; // Variation #1 is always the control
// The middle card's label, the same for every goal (live, waiting and History).
const EXTRA_LABEL = 'Extra Conversions';
const DAYS_PER_MONTH = 30.44;
// Where Visiopt's own value came from - the card and "How is this calculated?" name it alike.
const SOURCE_LABEL: Record<string, string> = {
  test: "from this test's sales",
  test_slow: "from this test's sales",
  campaign: 'from your campaign sales',
  past: 'from your past tests',
};
/* The basis named after a value, e.g. "from your past tests, first purchase only".
   A modeled purchase goal draws on the account's whole sales history. */
const basisLabel = (basis: string, source: string, modeled = false): string => {
  if (basis === 'manual' || (!basis && source === 'manual')) return 'your value';
  if (basis === 'avg_sale') return 'from your customer value';
  const from = modeled ? 'from your sales history' : (SOURCE_LABEL[source] || '');
  if (basis === 'first_purchase') return from ? `${from}, first purchase only` : 'first purchase only';
  return from; // results saved before the basis was stored
};
const basisTag = (basis: string, source: string, modeled = false) => {
  const label = basisLabel(basis, source, modeled);
  return label ? ` (${label})` : '';
};

const toNumber = (value: any) => Number(String(value ?? '').replace(/,/g, '')) || 0;
const formatCount = (n: number) => Math.round(n).toLocaleString('en-US');
const formatMoney = (n: number, decimals = 0) =>
  '$' + n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
// Card display only: $1,000,000 and up abbreviates to $1.72M; under $1M stays exact.
// "How is this calculated?" details always show the full number.
const formatMoneyCard = (n: number) => (n >= 1e6 ? '$' + (n / 1e6).toFixed(2) + 'M' : formatMoney(n));
const formatRate = (rate: number) => (rate * 100).toFixed(2) + '%';
const formatDate = (ts: number) =>
  new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
// The window a calculated value came from, e.g. "Oct 14, 2025 – Jul 5, 2026".
const formatRange = (from: number, to: number) => `${formatDate(from)} – ${formatDate(to)}`;
// A value per conversion always shows cents ($525.00), live and locked alike.
const formatValue = (n: number) => formatMoney(n, 2);
// "80%" and "2.51%" - a rate in a sentence, no trailing zeros.
const formatPctPlain = (rate: number) => `${Number((rate * 100).toFixed(2))}%`;
// The Edit form's nudge toward a customer value: long buying times and/or
// customers paying more than once both make first purchase read low.
const customerValueHint = (days: number, repeatShare: number, better = 'Entering what a customer is worth (payment plans included)') => {
  const slow = days > 30;
  const repeat = repeatShare >= 0.1;
  if (slow && repeat) return `💡 Your customers take about ${days} days to buy, and some pay over time. ${better} will likely be more accurate than first purchase.`;
  if (slow) return `💡 Your customers take about ${days} days to buy. ${better} will likely be more accurate than first purchase.`;
  if (repeat) return `💡 Some of your customers pay over time. ${better} will likely be more accurate than first purchase.`;
  return '';
};
const FIRST_PURCHASE_NOTE = 'First purchase only. If your system has payment plans or ongoing payments, enter what a customer is worth instead.';
// A purchase goal's form has no "What is a customer worth?" - it asks what one sale is worth.
const FIRST_PURCHASE_NOTE_SALE = 'First purchase only. If your system has payment plans or ongoing payments, enter what one sale is worth instead.';

/* Money amounts inside the plain-English story render in the revenue green. */
const Money = ({ n, decimals }: { n: number; decimals?: number }) => (
  <i className="vis-impact__money">{formatMoney(n, decimals ?? (Number.isInteger(n) ? 0 : 2))}</i>
);
/* "about $184,255" - the story always shows the full number (only the cards
   abbreviate $1M+), so a result next to a rounded count reads as an estimate. */
const AboutMoney = ({ n }: { n: number }) => <>about <Money n={n} decimals={0} /></>;

/* A worked line "a × b = c" in a tooltip: "about" when the rounded figures
   shown don't multiply to the exact result to the cent. */
const isExact = (shownA: number, shownB: number, result: number) =>
  Math.abs(Math.round(shownA * shownB * 100) - Math.round(result * 100)) < 1;
const pctShown = (rate: number) => Number((rate * 100).toFixed(2)) / 100;
const moneyShown = (n: number) => Math.round(n * 100) / 100;

/* "(how?)" - the working behind a number. Shows on mouse hover; a tap pins
   it open or closed, so it also works on phones. */
function HowTip({ label, children }: { label: string; children: React.ReactNode }) {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  return (
    <span
      className="vis-impact__howtip"
      onPointerEnter={(event) => { if (event.pointerType === 'mouse') setHovered(true); }}
      onPointerLeave={(event) => { if (event.pointerType === 'mouse') setHovered(false); }}
    >
      <button
        type="button"
        className="vis-impact__link vis-impact__howbtn"
        aria-label={label}
        aria-expanded={hovered || pinned}
        onClick={(event) => { event.preventDefault(); event.stopPropagation(); setPinned((open) => !open); }}
      >
        (how?)
      </button>
      {(hovered || pinned) && <span className="vis-impact__howpop" role="tooltip">{children}</span>}
    </span>
  );
}

export interface ImpactNumbers {
  leaderId: number;
  controlUniques: number;
  controlConversions: number;
  leaderUniques: number;
  leaderConversions: number;
  controlRate: number;
  leaderRate: number;
  lift: number | null;
  totalUniques: number;
  days: number;
  monthlyUniques: number;
  goalFound: number;
  isRevenueGoal: boolean;
  controlRevenuePerUnique: number;
  leaderRevenuePerUnique: number;
  valuePerConversion: number | null;
  valueSource: string;
  revenueFound: number | null;
  valueBasis?: string;        // 'first_purchase' | 'avg_sale' | 'manual' ('' on older saved results)
  avgSale?: number | null;    // the customer's average total sale per customer
  closeRate?: number | null;  // Visiopt's close rate: customers ÷ goal conversions
}

/* The step-8 model: a slow purchase goal estimated from an earlier step. */
interface ImpactModel {
  stepName: string;
  closeRate: number;
  valuePerCustomer: number;
  valuePerStep: number;
  autoValuePerStep: number;  // the first-purchase value per step, before the customer's choice
  completions: number;
  customers: number;
  stepControlRate: number;
  stepLeaderRate: number;
  manual: boolean;
  valueBasis: string;
  saleValue: number | null;  // the customer's "What is one sale worth?"
  stepFound: number;      // extra step conversions per month (test data)
  purchasesFound: number; // stepFound x closeRate
  revenueFound: number;   // stepFound x valuePerStep
}

function modelFromPayload(m: any): Omit<ImpactModel, 'stepFound' | 'purchasesFound' | 'revenueFound'> | null {
  if (!m) return null;
  return {
    stepName: m.step_name || '',
    closeRate: Number(m.close_rate) || 0,
    valuePerCustomer: Number(m.value_per_customer) || 0,
    valuePerStep: Number(m.value_per_step) || 0,
    autoValuePerStep: Number(m.auto_value_per_step ?? m.value_per_step) || 0,
    completions: Number(m.completions) || 0,
    customers: Number(m.customers) || 0,
    stepControlRate: Number(m.step_control_rate) || 0,
    stepLeaderRate: Number(m.step_leader_rate) || 0,
    manual: Number(m.manual) === 1,
    valueBasis: m.value_basis || (Number(m.manual) === 1 ? 'manual' : ''),
    saleValue: m.sale_value != null ? Number(m.sale_value) : null,
  };
}

/* The client's formulas, run on the same rows the variations table shows:
   monthly uniques = all uniques / days x 30.44
   goal found      = (leader conv. rate - control conv. rate) x monthly uniques
   revenue found   = goal found x value per conversion, or - for a purchase goal -
   (leader - control revenue per unique) x monthly uniques.
   isRevenueGoal is DECLARED (webhook "Count as Purchase"), passed by the caller;
   null falls back to inferring from the rows while the server answer loads. */
export function computeImpact(rows: any[], leaderId: number, days: number, valuePerConversion: number | null, valueSource = 'manual', isRevenueGoalOverride: boolean | null = null): ImpactNumbers | null {
  const control = rows.find((row) => Number(row.test_number) === CONTROL_ID);
  const leader = rows.find((row) => Number(row.test_number) === leaderId);
  if (!control || !leader || !(days > 0)) return null;

  const controlUniques = toNumber(control.unique);
  const leaderUniques = toNumber(leader.unique);
  const controlConversions = toNumber(control.pageconvnum);
  const leaderConversions = toNumber(leader.pageconvnum);
  const controlRate = controlUniques > 0 ? controlConversions / controlUniques : 0;
  const leaderRate = leaderUniques > 0 ? leaderConversions / leaderUniques : 0;
  const totalUniques = rows.reduce((sum, row) => sum + toNumber(row.unique), 0);
  const monthlyUniques = totalUniques / days * DAYS_PER_MONTH;
  const goalFound = (leaderRate - controlRate) * monthlyUniques;
  const isRevenueGoal = isRevenueGoalOverride !== null
    ? isRevenueGoalOverride
    : rows.some((row) => toNumber(row.revenue) > 0);
  const controlRevenuePerUnique = controlUniques > 0 ? toNumber(control.revenue) / controlUniques : 0;
  const leaderRevenuePerUnique = leaderUniques > 0 ? toNumber(leader.revenue) / leaderUniques : 0;
  let revenueFound: number | null = null;
  if (isRevenueGoal) {
    revenueFound = (leaderRevenuePerUnique - controlRevenuePerUnique) * monthlyUniques;
  } else if (valuePerConversion) {
    revenueFound = goalFound * valuePerConversion;
  }

  return {
    leaderId,
    controlUniques,
    controlConversions,
    leaderUniques,
    leaderConversions,
    controlRate,
    leaderRate,
    lift: controlRate > 0 ? (leaderRate - controlRate) / controlRate : null,
    totalUniques,
    days,
    monthlyUniques,
    goalFound,
    isRevenueGoal,
    controlRevenuePerUnique,
    leaderRevenuePerUnique,
    valuePerConversion: isRevenueGoal ? null : valuePerConversion,
    valueSource,
    revenueFound,
  };
}

/* The numbers saved at Set Winner, as test_history_optimize_report returns them. */
function numbersFromLocked(locked: any): ImpactNumbers {
  return {
    leaderId: Number(locked.leader_id) || 0,
    controlUniques: Number(locked.control_uniques) || 0,
    controlConversions: Number(locked.control_conversions) || 0,
    leaderUniques: Number(locked.leader_uniques) || 0,
    leaderConversions: Number(locked.leader_conversions) || 0,
    controlRate: Number(locked.control_rate) || 0,
    leaderRate: Number(locked.leader_rate) || 0,
    lift: locked.lift != null ? Number(locked.lift) : null,
    totalUniques: Number(locked.total_uniques) || 0,
    days: Number(locked.days) || 1,
    monthlyUniques: Number(locked.monthly_uniques) || 0,
    goalFound: Number(locked.goal_found) || 0,
    isRevenueGoal: Number(locked.is_revenue_goal) === 1,
    controlRevenuePerUnique: Number(locked.control_rpu) || 0,
    leaderRevenuePerUnique: Number(locked.leader_rpu) || 0,
    valuePerConversion: locked.value_per_conversion != null ? Number(locked.value_per_conversion) : null,
    valueSource: locked.value_source || '',
    revenueFound: locked.revenue_found != null ? Number(locked.revenue_found) : null,
    valueBasis: locked.value_basis || (locked.value_source === 'manual' ? 'manual' : ''),
    avgSale: locked.avg_sale != null ? Number(locked.avg_sale) : null,
    closeRate: locked.close_rate != null ? Number(locked.close_rate) : null,
  };
}

/* The Revenue card's resolved state for the selected goal, from the backend. */
interface GoalValueState {
  key: string;
  loading: boolean;
  loaded: boolean;
  state: string;              // standard goals: 'value' | 'collecting' | 'none' | ''
  value: number | null;
  source: string;
  slowNote: boolean;
  progress: { count: number; target: number } | null;
  hasAutoValue: boolean;
  introSeen: boolean;
  revenueTracked: boolean;
  isPurchaseGoal: boolean;    // declared: webhook "Count as Purchase"
  purchaseMode: string;       // 'measured' | 'measured_slow' | 'modeled' | 'collecting' | ''
  model: ReturnType<typeof modelFromPayload>;
  // What the value was based on — "How is this calculated?" names it in plain words.
  basis: { booked: number; customers: number; revenue: number; from: number; to: number; maturity?: number } | null;
  autoSource: string;         // where Visiopt's own value came from, even while the customer's is used
  valueBasis: string;         // 'first_purchase' | 'avg_sale' | 'manual'
  autoValue: number | null;   // Visiopt's first-purchase value, for the form
  closeRate: number | null;   // customers ÷ goal conversions, for the average-total-sale choice
  avgSale: number | null;
  purchaseHint: boolean;      // this goal: a webhook with sales but "Count as Purchase" off
  salesWebhooks: SalesWebhook[]; // no "Count as Purchase" webhook: the webhooks that send sale amounts
  hasCampaign: boolean;       // the account runs Visiopt campaigns
  timing: { days: number; buyers: number; fallback: boolean; kind: string } | null; // X: how long buyers take
  repeatShare: number;        // share of customers who paid more than once
  purchaseSources: string[];  // names of the "Count as Purchase" webhooks
  gate: ImpactGate | null;    // Step 10: a purchase goal too early / with too few sales
}

/* A webhook that sends sale amounts; key and processor open its edit screen in Settings. */
interface SalesWebhook {
  key: string;
  name: string;
  processor: string;
}

/* Step 10 - a purchase goal's Revenue Found waits until the test has run X days
   and each side has 10+ conversions; until then a message and an earlier estimate. */
interface ImpactGate {
  reason: string;             // 'too_early' | 'few_sales'
  xDays: number;
  testDays: number;
  controlConv: number;
  leaderConv: number;
  earlier: { goalType: string; goalKey: string; goalName: string; revenueFound: number } | null;
}
const GATE_MODES = ['too_early', 'few_sales'];
function gateFromPayload(mode: string, g: any): ImpactGate | null {
  if (!g || !GATE_MODES.includes(mode)) return null;
  return {
    reason: mode,
    xDays: Number(g.x_days) || 0,
    testDays: Number(g.test_days) || 0,
    controlConv: Number(g.control_conv) || 0,
    leaderConv: Number(g.leader_conv) || 0,
    earlier: g.earlier ? {
      goalType: g.earlier.goal_type || '',
      goalKey: g.earlier.goal_key || '',
      goalName: g.earlier.goal_name || '',
      revenueFound: Number(g.earlier.revenue_found) || 0,
    } : null,
  };
}
const EMPTY_GOAL_VALUE: GoalValueState = {
  key: '', loading: false, loaded: false, state: '', value: null, source: '',
  slowNote: false, progress: null, hasAutoValue: false, introSeen: true, revenueTracked: true,
  isPurchaseGoal: false, purchaseMode: '', model: null,
  basis: null, autoSource: '',
  valueBasis: '', autoValue: null, closeRate: null, avgSale: null, purchaseHint: false, salesWebhooks: [], hasCampaign: false,
  timing: null, repeatShare: 0, purchaseSources: [], gate: null,
};

interface ImpactSummaryProps {
  rows?: any[];               // the variations table rows on screen
  days?: number;              // days of data behind those rows
  predictiveState?: any;      // Predictive Winner Detection result for the selected goal
  goalName: string;           // the goal picked in "Conversions to"
  goalKey: string;            // goal type + id; a value per conversion belongs to one goal
  singleGoal?: boolean;       // false for the "All ..." views, which have no one goal to project
  selectedMemberWebsite?: string;
  goalType?: string;          // base goal type for the value API
  goalId?: string;            // encrypted goal id for the value API
  pageId?: string;            // the test page; the speed check runs on this test
  locked?: boolean;           // Test History: render the saved numbers, never recalculate
  lockedData?: any;           // the saved numbers for the selected goal
  lockedDate?: string;        // when the winner was set
  onSelectGoal?: (goalType: string, goalKey: string) => void; // switch "Conversions to" (Step 10's earlier estimate link)
}

export function ImpactSummary({
  rows = [], days = 0, predictiveState = null, goalName, goalKey, singleGoal = true,
  selectedMemberWebsite = '', goalType = '', goalId = '', pageId = '',
  locked = false, lockedData = null, lockedDate = '', onSelectGoal,
}: ImpactSummaryProps) {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const isDark = theme.colorScheme === 'dark';
  // Open or closed is remembered per user, like the page's Expand setting.
  const [collapsed, setCollapsed] = useState<boolean>(() => visiUserSession()?.impact_summary_collapsed === true);
  const [howOpen, setHowOpen] = useState(false);
  // "Show details" inside the calculation panel; closed by default every time.
  const [howDetails, setHowDetails] = useState(false);
  const [editing, setEditing] = useState(false);
  // "Choose your sales webhook": the list of webhooks that send sale amounts.
  const [chooserOpen, setChooserOpen] = useState(false);
  const [editLabel, setEditLabel] = useState('');
  const [draftValue, setDraftValue] = useState('');
  // The form's "Value based on" choice: Visiopt's first purchase, an average total sale, or an exact value.
  const [editChoice, setEditChoice] = useState<'first' | 'avg' | 'own'>('first');
  const [draftAvg, setDraftAvg] = useState('');
  const [goalValue, setGoalValue] = useState<GoalValueState>(EMPTY_GOAL_VALUE);

  // A different goal brings different numbers - close the form and the breakdown.
  useEffect(() => {
    setEditing(false);
    setHowOpen(false);
    setHowDetails(false);
  }, [goalKey]);

  const leaderState = !locked && predictiveState ? LEADER_STATES[predictiveState.state] : undefined;
  const liveLeaderId = leaderState && predictiveState.leading_variation_id != null
    ? Number(predictiveState.leading_variation_id) : null;

  // The answer must be for this goal AND this leader: a purchase goal is modeled
  // only once the server knows the leader.
  const fetchKey = `${goalKey}|L${liveLeaderId ?? 0}`;
  const latestFetchKey = useRef(fetchKey);
  latestFetchKey.current = fetchKey;
  const serverLoaded = goalValue.loaded && goalValue.key === fetchKey;
  // Without these the value API isn't called; the cards then work from the table alone.
  const canFetch = !!(selectedMemberWebsite && goalType && goalId);
  const serverReady = serverLoaded || !canFetch;
  const isPurchase = serverLoaded ? goalValue.isPurchaseGoal : null;
  // A slow purchase goal whose earlier step shows no lift for the leader: no extra
  // purchases expected yet, so no numbers - never a guess from the test's few sales.
  const noLift = !locked && serverLoaded && goalValue.isPurchaseGoal && goalValue.purchaseMode === 'no_lift';
  // Step 10: a purchase goal too early / with too few sales - no revenue figure, a message instead.
  const gate: ImpactGate | null = locked
    ? (lockedData ? gateFromPayload(lockedData.purchase_mode || '', lockedData.gate) : null)
    : (serverLoaded && goalValue.isPurchaseGoal ? goalValue.gate : null);

  let impact: ImpactNumbers | null = null;
  let hasNumbers = false;
  let model: ImpactModel | null = null;
  if (locked) {
    impact = lockedData ? numbersFromLocked(lockedData) : null;
    hasNumbers = !!impact && Number(lockedData.positive) === 1 && impact.lift !== null;
    if (hasNumbers && impact && lockedData.purchase_mode === 'modeled' && lockedData.model) {
      const m = modelFromPayload(lockedData.model);
      if (m) {
        model = {
          ...m,
          stepFound: Number(lockedData.model.step_found) || 0,
          purchasesFound: Number(lockedData.goal_found) || 0,
          revenueFound: Number(lockedData.revenue_found) || 0,
        };
      }
    }
  } else if (singleGoal && liveLeaderId !== null && Array.isArray(rows)) {
    impact = computeImpact(
      rows, liveLeaderId, days,
      serverLoaded && goalValue.state === 'value' ? goalValue.value : null,
      goalValue.source, isPurchase,
    );
    if (impact && serverLoaded) {
      impact.valueBasis = goalValue.valueBasis;
      impact.avgSale = goalValue.avgSale;
      impact.closeRate = goalValue.closeRate;
      if (gate) {
        impact.revenueFound = null;
      }
    }
    // Never show a negative: the numbers appear only while the leader beats the control.
    // Nor a guess: the server decides purchase vs. standard and measured vs. modeled.
    hasNumbers = serverReady && !noLift && !!impact && impact.leaderId !== CONTROL_ID
      && impact.leaderRate > impact.controlRate && impact.lift !== null;
    /* Step 8 — a slow purchase goal is modeled from the earlier step's test result,
       so the cards hold even when the purchases themselves are still trickling in. */
    if (impact && serverLoaded && goalValue.isPurchaseGoal
        && goalValue.purchaseMode === 'modeled' && goalValue.model
        && impact.leaderId !== CONTROL_ID) {
      const m = goalValue.model;
      const stepFound = (m.stepLeaderRate - m.stepControlRate) * impact.monthlyUniques;
      if (stepFound > 0) {
        model = {
          ...m,
          stepFound,
          purchasesFound: stepFound * m.closeRate,
          revenueFound: stepFound * m.valuePerStep,
        };
        hasNumbers = true;
      }
    }
  }

  // keepCurrent: after a save, the cards keep their numbers until the new ones arrive.
  const fetchGoalValue = (key: string, keepCurrent = false) => {
    setGoalValue((prev) => (keepCurrent
      ? { ...prev, loading: true }
      : { ...EMPTY_GOAL_VALUE, introSeen: prev.introSeen, key, loading: true }));
    // An answer that arrives after the goal or leader changed is dropped; the
    // effect below then asks again for the current one.
    const isStale = () => key !== latestFetchKey.current;
    const dropStale = () => setGoalValue((prev) => ({ ...prev, loading: false }));
    TestsApi.getimpactgoalvalue({
      system_website_id: selectedMemberWebsite, goal_type: goalType, goal_id: goalId,
      page_id: pageId, leader_id: liveLeaderId ?? 0,
    })
      .then((response: any) => (response.ok ? response.json() : null))
      .then((data: any) => {
        if (isStale()) {
          dropStale();
        } else if (data && data.status === 1) {
          const d = data.data;
          setGoalValue({
            key,
            loading: false,
            loaded: true,
            state: d.state || '',
            value: d.value != null ? Number(d.value) : null,
            source: d.source || '',
            slowNote: Number(d.slow_note) === 1,
            progress: d.progress || null,
            hasAutoValue: Number(d.has_auto_value) === 1,
            introSeen: Number(d.intro_seen) === 1,
            revenueTracked: d.revenue_tracked != null ? Number(d.revenue_tracked) === 1 : true,
            isPurchaseGoal: Number(d.is_purchase_goal) === 1,
            purchaseMode: d.purchase_mode || '',
            model: modelFromPayload(d.model),
            basis: d.basis ? {
              booked: Number(d.basis.booked) || 0,
              customers: Number(d.basis.with_revenue) || 0,
              revenue: Number(d.basis.revenue) || 0,
              from: Number(d.basis.from) || 0,
              to: Number(d.basis.to) || 0,
              maturity: d.basis.maturity != null ? Number(d.basis.maturity) : undefined,
            } : null,
            autoSource: d.auto_source || d.source || '',
            valueBasis: d.value_basis || '',
            autoValue: d.auto_value != null ? Number(d.auto_value) : null,
            closeRate: d.close_rate != null ? Number(d.close_rate) : null,
            avgSale: d.avg_sale != null ? Number(d.avg_sale) : null,
            purchaseHint: Number(d.purchase_hint) === 1,
            salesWebhooks: Array.isArray(d.sales_webhooks) ? d.sales_webhooks : [],
            hasCampaign: Number(d.has_campaign) === 1,
            timing: d.timing ? {
              days: Number(d.timing.days) || 0,
              buyers: Number(d.timing.buyers) || 0,
              fallback: Number(d.timing.fallback) === 1,
              kind: d.timing.kind || '',
            } : null,
            repeatShare: Number(d.repeat_share) || 0,
            purchaseSources: Array.isArray(d.purchase_sources) ? d.purchase_sources : [],
            gate: gateFromPayload(d.purchase_mode || '', d.gate),
          });
        } else {
          setGoalValue((prev) => ({ ...EMPTY_GOAL_VALUE, introSeen: prev.introSeen, key, loaded: true }));
        }
      })
      .catch(() => (isStale()
        ? dropStale()
        : setGoalValue((prev) => ({ ...EMPTY_GOAL_VALUE, introSeen: prev.introSeen, key, loaded: true }))));
  };

  /* The Revenue card runs its checks from the first render — before a leader too,
     so "no revenue data" and "calculating" show while waiting (Revenue Logic step 1). */
  useEffect(() => {
    if (locked || !singleGoal || !selectedMemberWebsite || !goalType || !goalId) return;
    if (goalValue.key !== fetchKey && !goalValue.loading) {
      fetchGoalValue(fetchKey);
    }
    // goalValue.loading: when an in-flight answer finishes (or is dropped as stale), check again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey, selectedMemberWebsite, goalType, goalId, locked, singleGoal, goalValue.loading]);

  /* The intro dismisses itself the first time real numbers show. */
  useEffect(() => {
    if (!locked && hasNumbers && goalValue.loaded && !goalValue.introSeen) {
      setGoalValue((prev) => ({ ...prev, introSeen: true }));
      TestsApi.saveimpactgoalvalue({ system_website_id: selectedMemberWebsite, goal_type: goalType, goal_id: goalId, mode: 'intro_got_it' }).catch(() => null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNumbers, goalValue.loaded]);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    setHowOpen(false);
    setEditing(false);
    setVisiUserSession('impact_summary_collapsed', next);
  };
  const dismissIntro = () => {
    setGoalValue((prev) => ({ ...prev, introSeen: true }));
    TestsApi.saveimpactgoalvalue({ system_website_id: selectedMemberWebsite, goal_type: goalType, goal_id: goalId, mode: 'intro_got_it' }).catch(() => null);
  };
  /* The modeled purchase card asks what one sale is worth (Visiopt turns it
     into a value per step with the step's close rate); every other card asks
     for the value per conversion, with three choices. */
  const editingModel = goalValue.isPurchaseGoal && goalValue.purchaseMode === 'modeled' ? goalValue.model : null;
  const currentBasis = editingModel ? editingModel.valueBasis : goalValue.valueBasis;
  // The choices need Visiopt's own numbers; without them the form is a plain value box.
  const hasChoice = editingModel ? true : goalValue.hasAutoValue;
  const openValueForm = (label = '') => {
    if (editingModel) {
      setDraftValue(editingModel.saleValue ? String(editingModel.saleValue) : '');
      setDraftAvg('');
      setEditChoice(editingModel.valueBasis === 'manual' ? 'own' : 'first');
    } else {
      setDraftValue(goalValue.source === 'manual' && goalValue.value ? String(goalValue.value) : '');
      setDraftAvg(goalValue.avgSale ? String(goalValue.avgSale) : '');
      setEditChoice(!hasChoice || currentBasis === 'manual' ? 'own' : currentBasis === 'avg_sale' ? 'avg' : 'first');
    }
    setEditLabel(label);
    setEditing(true);
  };
  const saveChoice = (mode: string, value?: number) => {
    setEditing(false);
    TestsApi.saveimpactgoalvalue({ system_website_id: selectedMemberWebsite, goal_type: goalType, goal_id: goalId, mode, value })
      .then(() => fetchGoalValue(fetchKey, true))
      .catch(() => null);
  };
  const saveValue = (event: React.FormEvent) => {
    event.preventDefault();
    if (editChoice === 'first') {
      if (currentBasis === 'first_purchase') {
        setEditing(false);
      } else {
        saveChoice('use_visiopt');
      }
      return;
    }
    const value = parseFloat(editChoice === 'avg' ? draftAvg : draftValue);
    if (!(value > 0)) return;
    saveChoice(editChoice === 'avg' ? 'avg_sale' : 'value', value);
  };
  const openCampaignSetup = () => navigate('/manage-campaigns');
  // The webhook's edit screen in Settings, scrolled to "Count as Purchase".
  const openWebhook = (w: SalesWebhook) => navigate('/settings', {
    state: { from: 'payments_processing_and_carts', webhookKey: w.key, webhookProcessor: w.processor },
  });

  const vars = {
    '--imp-panel': isDark ? theme.colors.dark[6] : '#fff',
    '--imp-border': isDark ? 'rgba(255,255,255,.06)' : theme.colors.gray[2],
    '--imp-glow': isDark
      ? 'radial-gradient(70% 120% at 0% 0%, rgba(144,146,150,.10), transparent 60%)'
      : 'radial-gradient(70% 120% at 0% 0%, rgba(134,142,150,.08), transparent 60%)',
    // Light mode uses the product's existing light card shades (plain surface +
    // gray border), not the dark preview's gradient treatment.
    '--imp-box-bg': isDark
      ? 'linear-gradient(160deg, rgba(144,146,150,.14), rgba(255,255,255,.015) 60%)'
      : theme.colors.gray[0],
    '--imp-box-line': isDark ? 'rgba(144,146,150,.22)' : theme.colors.gray[3],
    '--imp-strong': isDark ? '#fff' : theme.colors.dark[7],
    '--imp-ink': isDark ? theme.colors.dark[0] : theme.colors.gray[8],
    '--imp-muted': isDark ? theme.colors.dark[2] : theme.colors.gray[6],
    // Raised for dark-mode readability (annualized line, form help, recommendation).
    '--imp-dim': isDark ? theme.colors.dark[2] : theme.colors.gray[6],
    '--imp-line2': isDark ? theme.colors.dark[5] : theme.colors.gray[2],
    '--imp-pot': isDark ? 'rgb(200,201,203)' : theme.colors.gray[7],
    '--imp-link': isDark ? 'rgb(200,201,203)' : theme.colors.gray[7],
    '--imp-cta': isDark ? theme.colors.dark[2] : theme.colors.gray[7],
    '--imp-cta-shadow': isDark ? 'rgba(144,146,150,.25)' : 'rgba(73,80,87,.20)',
    '--imp-chip-bg': isDark ? 'rgba(255,255,255,.05)' : 'rgba(0,0,0,.03)',
    '--imp-chip-line': isDark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.08)',
    '--imp-panel2-bg': isDark ? 'rgba(0,0,0,.18)' : theme.colors.gray[0],
    '--imp-panel2-line': isDark ? 'rgba(255,255,255,.06)' : theme.colors.gray[2],
    '--imp-hover': isDark ? theme.colors.dark[5] : theme.colors.gray[1],
    '--imp-input-bg': isDark ? theme.colors.dark[6] : '#fff',
    '--imp-input-line': isDark ? theme.colors.dark[4] : theme.colors.gray[4],
    // Revenue green = the Set Winner green (STATUS.good) in both modes. The BETA
    // badge needs a darker ink on the light theme or it washes out.
    '--imp-green': STATUS.good,
    '--imp-green-bg': withAlpha(STATUS.good, isDark ? 0.22 : 0.15),
    '--imp-green-line': withAlpha(STATUS.good, 0.5),
    '--imp-beta-ink': isDark ? STATUS.good : theme.colors.green[9],
    '--imp-track': isDark ? theme.colors.dark[4] : theme.colors.gray[3],
    // Slow-sales note box: the Predictive "Caution" amber, live and locked alike.
    '--imp-amber': STATUS.warning,
    '--imp-amber-bg': withAlpha(STATUS.warning, isDark ? 0.12 : 0.1),
    '--imp-amber-line': withAlpha(STATUS.warning, 0.45),
    // The Predictive Winner Detection note colours, for the "pick one goal" message.
    '--imp-info-edge': isDark ? '#adb5bd' : theme.colors.gray[5],
    '--imp-info-line': isDark ? '#46416d' : 'transparent',
    '--imp-info-bg': isDark ? '#2b2b3c' : theme.colors.gray[0],
    '--imp-info-ink': isDark ? '#b8bcc8' : theme.colors.gray[6],
  } as React.CSSProperties;

  function renderValueForm() {
    const unit = editLabel || goalName || 'conversion';
    if (!hasChoice) {
      // No Visiopt numbers yet: the customer can only enter a value.
      return (
        <form className="vis-impact__form" onSubmit={saveValue}>
          <label htmlFor="vis-impact-value">{`What is one ${unit} worth?`}</label>
          <div className="vis-impact__inrow">
            <span>$</span>
            <input
              id="vis-impact-value"
              className="vis-impact__inp"
              type="number"
              min="0.01"
              step="any"
              value={draftValue}
              onChange={(event) => setDraftValue(event.target.value)}
              autoFocus
            />
            <span>per {unit}</span>
            <button className="vis-impact__cta" type="submit" disabled={!(parseFloat(draftValue) > 0)}>Save value</button>
            <button className="vis-impact__link" type="button" onClick={() => setEditing(false)}>Cancel</button>
          </div>
          <span className="vis-impact__note">
            Saved for this goal across all your tests. Only needed if Visiopt can't calculate it, for example when sales happen offline.
          </span>
        </form>
      );
    }
    /* "recorded by …": one or two purchase webhooks by name; three or more as a
       count, with the names on hover. */
    const sources = goalValue.purchaseSources;
    const recordedBy = sources.length >= 3
      ? <span className="vis-impact__abbr" title={sources.join(', ')}>your {sources.length} purchase webhooks</span>
      : sources.length
        ? <>{sources.map((name) => `“${name}”`).join(' and ')}</>
        : <>your purchase webhooks</>;
    const canSave = editChoice === 'first'
      || (editChoice === 'avg' ? parseFloat(draftAvg) > 0 : parseFloat(draftValue) > 0);
    const amountInput = (value: string, onFocus: () => void, onChange: (v: string) => void, label: string) => (
      <input
        className="vis-impact__inp"
        type="number"
        min="0.01"
        step="any"
        aria-label={label}
        value={value}
        onFocus={onFocus}
        onChange={(event) => onChange(event.target.value)}
      />
    );
    const buttons = (
      <div className="vis-impact__inrow">
        <button className="vis-impact__cta" type="submit" disabled={!canSave}>Save value</button>
        <button className="vis-impact__link" type="button" onClick={() => setEditing(false)}>Cancel</button>
      </div>
    );
    if (editingModel) {
      /* A purchase goal: "a customer" and "one sale" are the same thing, so one question. */
      const hint = customerValueHint(goalValue.timing ? goalValue.timing.days : 0, 0, 'Entering what one sale is worth (payment plans included)');
      return (
        <form className="vis-impact__form" onSubmit={saveValue}>
          <span className="vis-impact__formhead">What is each {goalName || 'sale'} worth?</span>
          <label className="vis-impact__opt">
            <input type="radio" name="vis-impact-basis" checked={editChoice === 'first'} onChange={() => setEditChoice('first')} />
            <span>
              Use my sales data (recommended)
              <span className="vis-impact__optval"> – {formatValue(editingModel.valuePerCustomer)} per sale, from first purchases recorded by {recordedBy}</span>
            </span>
          </label>
          <label className="vis-impact__opt">
            <input type="radio" name="vis-impact-basis" checked={editChoice === 'own'} onChange={() => setEditChoice('own')} />
            <span className="vis-impact__optbody">
              <span className="vis-impact__inrow">
                What is one sale worth? –
                <span className="vis-impact__amt">$ {amountInput(draftValue, () => setEditChoice('own'), setDraftValue, 'Value of one sale')} per sale</span>
              </span>
              <span className="vis-impact__optval">
                Visiopt works out the value per {editingModel.stepName} ({formatPctPlain(editingModel.closeRate)} of {editingModel.stepName} become customers).
              </span>
              {hint && <span className="vis-impact__tip">{hint}</span>}
            </span>
          </label>
          {buttons}
        </form>
      );
    }
    const firstValue = goalValue.autoValue;
    const closeRate = goalValue.closeRate;
    const hint = customerValueHint(goalValue.timing ? goalValue.timing.days : 0, goalValue.repeatShare);
    /* The "(how?)" workings use the same numbers as "How is this calculated?". */
    const b = goalValue.basis;
    const src = goalValue.autoSource;
    const range = b && b.from && b.to ? ` (${formatRange(b.from, b.to)})` : '';
    const where = src === 'campaign' ? `in your campaigns${range}`
      : src === 'past' ? `in your past tests${range}` : 'in this test';
    // An after-sale goal (thank-you, upsell) is valued by the purchase made just before it.
    const afterSale = !!goalValue.timing && goalValue.timing.kind === 'post';
    const canExplain = !!(b && b.booked > 0 && b.customers > 0 && firstValue && closeRate);
    const avgFirst = b && b.customers > 0 ? b.revenue / b.customers : 0;
    const example = parseFloat(draftAvg) > 0 ? parseFloat(draftAvg) : 1000;
    const money = (n: number) => formatMoney(n, Number.isInteger(moneyShown(n)) ? 0 : 2);
    return (
      <form className="vis-impact__form" onSubmit={saveValue}>
        <span className="vis-impact__formhead">What is each {unit} worth?</span>
        <label className="vis-impact__opt">
          <input type="radio" name="vis-impact-basis" checked={editChoice === 'first'} onChange={() => setEditChoice('first')} />
          <span>
            Use my sales data (recommended)
            {firstValue ? (
              <span className="vis-impact__optval">
                {' '}– Visiopt calculates it: {formatValue(firstValue)} per {unit}
                {canExplain && b && closeRate && firstValue ? (
                  <>
                    {' '}
                    <HowTip label={`How we got ${formatValue(firstValue)}`}>
                      <b>How we got {formatValue(firstValue)}</b>
                      <span>{formatCount(b.booked)} people reached {unit} {where}.</span>
                      <span>{formatCount(b.customers)} of them {afterSale ? 'bought just before' : 'became customers'} ({formatPctPlain(closeRate)}).</span>
                      <span>{afterSale ? 'That purchase' : 'Their first purchase'} averaged {formatValue(avgFirst)}.</span>
                      <span>
                        {formatPctPlain(closeRate)} × {formatValue(avgFirst)} = {isExact(pctShown(closeRate), moneyShown(avgFirst), firstValue) ? '' : 'about '}{formatValue(firstValue)} per {unit}.
                      </span>
                      <span>First purchase only. If customers pay over time, choose “What is a customer worth?”</span>
                    </HowTip>
                  </>
                ) : null}
                , from first purchases recorded by {recordedBy}.
              </span>
            ) : null}
          </span>
        </label>
        <label className="vis-impact__opt">
          <input type="radio" name="vis-impact-basis" checked={editChoice === 'avg'} onChange={() => setEditChoice('avg')} />
          <span className="vis-impact__optbody">
            <span className="vis-impact__inrow">
              What is a customer worth? – You enter the value of a customer:
              <span className="vis-impact__amt">$ {amountInput(draftAvg, () => setEditChoice('avg'), setDraftAvg, 'Value of one customer')} per customer</span>
              <span>(include payment plans).</span>
            </span>
            <span className="vis-impact__optval">
              Visiopt works out the value per {unit}
              {closeRate ? (
                <>
                  {' '}({formatPctPlain(closeRate)} of {unit} {afterSale ? 'bought just before' : 'become customers'}
                  {canExplain && b ? (
                    <>
                      {' '}
                      <HowTip label={`How we got ${formatPctPlain(closeRate)}`}>
                        <b>How we got {formatPctPlain(closeRate)}</b>
                        <span>{formatCount(b.booked)} people reached {unit} {where}.</span>
                        <span>
                          {formatCount(b.customers)} of them {afterSale
                            ? 'bought just before, in the same visit (from your purchase webhooks)'
                            : 'went on to buy (first purchase, from your purchase webhooks)'}.
                        </span>
                        <span>{formatCount(b.customers)} ÷ {formatCount(b.booked)} = {formatPctPlain(closeRate)} {afterSale ? 'bought just before' : 'become customers'}.</span>
                        <span>
                          So each {unit} is worth {formatPctPlain(closeRate)} of a customer. Example: {money(example)} × {formatPctPlain(closeRate)} = {isExact(moneyShown(example), pctShown(closeRate), example * closeRate) ? '' : 'about '}{money(example * closeRate)} per {unit}.
                        </span>
                      </HowTip>
                    </>
                  ) : null}
                  )
                </>
              ) : null}.
            </span>
            {hint && <span className="vis-impact__tip">{hint}</span>}
          </span>
        </label>
        <label className="vis-impact__opt">
          <input type="radio" name="vis-impact-basis" checked={editChoice === 'own'} onChange={() => setEditChoice('own')} />
          <span className="vis-impact__inrow">
            I know what this goal is worth – You enter the value of the goal:
            <span className="vis-impact__amt">$ {amountInput(draftValue, () => setEditChoice('own'), setDraftValue, `Value per ${unit}`)} per {unit}.</span>
            <span>Visiopt uses it exactly as entered.</span>
          </span>
        </label>
        {buttons}
      </form>
    );
  }

  /* A webhook with recorded sales but "Count as Purchase" off. Shown in every
     Revenue card state once there are numbers, since the panel it opens needs them. */
  function renderPurchaseHint() {
    if (locked || !hasNumbers || !serverLoaded || !goalValue.purchaseHint) return null;
    return (
      <button className="vis-impact__link vis-impact__hint" type="button" onClick={() => setHowOpen(true)}>
        Measure exact sales: turn on “Count as Purchase”
      </button>
    );
  }

  function renderCollectingHero(progress: { count: number; target: number } | null) {
    const count = progress ? progress.count : 0;
    const target = progress ? progress.target : 20;
    // A slow purchase goal with no earlier step to model from: suggest a customer value.
    const purchaseTip = goalValue.isPurchaseGoal && goalValue.timing
      ? customerValueHint(goalValue.timing.days, 0, 'Entering what one sale is worth (payment plans included)') : '';
    return (
      <>
        <span className="vis-impact__unlock is-quiet">Calculating Revenue Found</span>
        <span className="vis-impact__sub">Revenue Found appears once enough sales come in.</span>
        <div className="vis-impact__progress">
          {/* One segment per sale, so the bar reads the same as "X of 20 sales". */}
          <span className="vis-impact__segbar" role="img" aria-label={`${count} of ${target} sales`}>
            {Array.from({ length: target }, (_, i) => <i key={i} className={i < count ? 'is-on' : undefined} />)}
          </span>
          <span>{count} of {target} sales</span>
          <button className="vis-impact__link" type="button" onClick={() => openValueForm()}>Set a value instead</button>
        </div>
        {purchaseTip && <span className="vis-impact__tip">{purchaseTip}</span>}
        <span className="vis-impact__note">Run Visiopt campaigns to see Revenue Found from day one, even with long sales cycles.</span>
        {renderPurchaseHint()}
      </>
    );
  }

  /* No revenue data. The next step depends on the account: no "Count as
     Purchase" webhook but webhooks that send sale amounts -> name them and link
     to each one's settings (the client picks, Visiopt never guesses); this goal
     a sale webhook -> turn on "Count as Purchase"; campaigns but no sales ->
     revenue tracking; neither -> the campaigns button. */
  function renderNoDataHero() {
    const sales = serverLoaded ? goalValue.salesWebhooks : [];
    const enterValue = (label: string) => (
      <button className="vis-impact__link" type="button" onClick={() => openValueForm()}>{label}</button>
    );
    let sub: React.ReactNode;
    let actions: React.ReactNode;
    if (sales.length > 0) {
      const quoted = (w: SalesWebhook) => `“${w.name}”`;
      sub = sales.length === 1
        ? <>Your webhook {quoted(sales[0])} sends sale amounts. If it records your sales, mark it as a purchase and Visiopt calculates this from your real sales.</>
        : (
          <>
            Visiopt found {sales.length === 2
              ? <>2 webhooks that send sale amounts: {quoted(sales[0])} and {quoted(sales[1])}</>
              : <><span className="vis-impact__names" title={sales.map((w) => w.name).join(', ')}>your {sales.length} webhooks</span> that send sale amounts</>}.
            {' '}Mark each one that records your sales as a purchase, and Visiopt calculates this from your real sales.
            {' '}If two report the same order, choose only one.
          </>
        );
      actions = (
        <>
          {sales.length === 1 ? (
            <button className="vis-impact__outline" type="button" onClick={() => openWebhook(sales[0])}>Review this webhook →</button>
          ) : (
            <button className="vis-impact__outline" type="button" aria-expanded={chooserOpen} onClick={() => setChooserOpen((open) => !open)}>
              Choose your sales webhook →
            </button>
          )}
          {/* Right under the button: each name opens that webhook's edit screen with "Count as Purchase" highlighted. */}
          {chooserOpen && sales.length > 1 && (
            <div className="vis-impact__chooser">
              {sales.map((w) => (
                <button key={w.key} className="vis-impact__link" type="button" onClick={() => openWebhook(w)}>{w.name} →</button>
              ))}
            </div>
          )}
          {enterValue(`or enter what one ${goalName || 'conversion'} is worth`)}
        </>
      );
    } else {
      const salesWebhook = serverLoaded && goalValue.purchaseHint;
      const showCampaigns = !salesWebhook && !(serverLoaded && goalValue.hasCampaign);
      sub = salesWebhook
        ? 'Turn on “Count as Purchase” on your sale webhook and Visiopt calculates this automatically.'
        : showCampaigns
          ? 'Set up Visiopt campaigns or revenue tracking and Visiopt calculates this automatically.'
          : 'Set up revenue tracking and Visiopt calculates this automatically.';
      actions = (
        <>
          {showCampaigns && <button className="vis-impact__outline" type="button" onClick={openCampaignSetup}>Set up Visiopt campaigns</button>}
          {enterValue(showCampaigns ? 'or enter a value per conversion' : 'Enter a value per conversion')}
        </>
      );
    }
    return (
      <>
        <span className="vis-impact__unlock is-quiet">See what this lift is worth</span>
        <span className="vis-impact__sub">{sub}</span>
        <div className="vis-impact__actions">{actions}</div>
      </>
    );
  }

  /* Step 10: a purchase goal too early to measure, or with fewer than 10
     conversions a side - no dollar figure. The message ends with the test's goal
     with the most conversions that already shows Revenue Found, when there is one;
     on the live card its name switches "Conversions to" to that goal. */
  function renderGateHero(g: ImpactGate) {
    const e = g.earlier;
    const dayWord = (d: number) => `${d} ${d === 1 ? 'day' : 'days'}`;
    const earlierGoal = e && !locked && onSelectGoal && e.goalKey
      ? <button className="vis-impact__link" type="button" onClick={() => onSelectGoal(e.goalType, e.goalKey)}>{e.goalName}</button>
      : <b>{e ? e.goalName : ''}</b>;
    const earlierLine = e ? <> For an earlier estimate, see {earlierGoal}: {formatMoneyCard(e.revenueFound)}/mo.</> : null;
    return g.reason === 'too_early' ? (
      <>
        <span className="vis-impact__unlock is-quiet">Too early to measure sales on this step</span>
        <span className="vis-impact__sub">
          Sales here take about {dayWord(g.xDays)} to arrive. This test has run {dayWord(g.testDays)}, so the lift hasn't reached this step yet.{earlierLine}
        </span>
      </>
    ) : (
      <>
        <span className="vis-impact__unlock is-quiet">Not enough sales yet to measure the lift</span>
        <span className="vis-impact__sub">
          {formatCount(g.controlConv)} vs. {formatCount(g.leaderConv)} so far. Revenue Found appears once each side has 10.{earlierLine}
        </span>
      </>
    );
  }

  function renderHero(n: ImpactNumbers) {
    if (!locked && editing && !n.isRevenueGoal) {
      return renderValueForm();
    }
    if (gate && n.isRevenueGoal) {
      return renderGateHero(gate);
    }
    const projected = locked ? 'Projected monthly at traffic when winner was set' : 'Projected monthly at current traffic';
    /* A slow purchase goal without a modelable earlier step shows the calculating card. */
    if (!locked && n.isRevenueGoal && serverLoaded && goalValue.purchaseMode === 'collecting') {
      return renderCollectingHero(goalValue.progress);
    }
    if (n.revenueFound !== null && n.revenueFound > 0) {
      const vpc = n.valuePerConversion;
      return (
        <>
          <span className="vis-impact__val is-hero">{formatMoneyCard(n.revenueFound)}<small>/mo</small></span>
          <span className="vis-impact__sub">
            {n.isRevenueGoal || !vpc ? projected : (
              // The card names the actual source, the same words as the panel - live and locked.
              // Edit is always available while the test runs; it goes away once a winner is set.
              <>
                Estimated {formatValue(vpc)} value per {goalName || 'conversion'}{basisTag(n.valueBasis || '', n.valueSource)}
                {!locked ? (
                  <> · <button className="vis-impact__link" type="button" onClick={() => openValueForm()}>Edit</button></>
                ) : null}
              </>
            )}
          </span>
          {/* Each goal gives its own estimate; the card says which one this is. */}
          <span className="vis-impact__basedon">Based on {goalName || 'this goal'}</span>
          <span className="vis-impact__note">Annualized run rate {formatMoneyCard(n.revenueFound * 12)}</span>
          {/* Below the numbers, so they stay together. */}
          {renderPurchaseHint()}
        </>
      );
    }
    if (n.isRevenueGoal && n.revenueFound !== null) {
      // A purchase goal where the leader earns less per visitor than the control.
      return (
        <>
          <span className="vis-impact__val is-none">—</span>
          <span className="vis-impact__sub">No extra revenue per visitor{locked ? '' : ' yet'}</span>
        </>
      );
    }
    if (locked) {
      return (
        <>
          <span className="vis-impact__val is-none">—</span>
          <span className="vis-impact__sub">No value per conversion was available when the winner was set.</span>
        </>
      );
    }
    if (goalValue.loading || !serverLoaded) {
      return <span className="vis-impact__val is-none">—</span>;
    }
    if (goalValue.state === 'collecting') {
      return renderCollectingHero(goalValue.progress);
    }
    // No revenue data of any kind on this account.
    return renderNoDataHero();
  }

  /* A card with no number yet: "—" and an optional line under it. */
  function dashCard(icon: React.ReactNode, label: string, body?: React.ReactNode) {
    return (
      <div className="vis-impact__col">
        <span className="vis-impact__pot is-hidden" aria-hidden="true">POTENTIAL</span>
        <span className="vis-impact__lbl" title={label}>{icon}<span>{label}</span></span>
        {body || (
          <>
            <span className="vis-impact__val is-none">—</span>
            <span className="vis-impact__sub">Waiting for a leader</span>
          </>
        )}
      </div>
    );
  }
  const purchaseIconGuess = () => (isPurchase !== null ? isPurchase : (Array.isArray(rows) && rows.some((row) => toNumber(row.revenue) > 0)));

  /* A leader is named but the server hasn't answered yet: dashes, not a guess -
     a slow purchase goal would otherwise flash the measured view, then switch. */
  function renderLoading() {
    const dash = <span className="vis-impact__val is-none">—</span>;
    return (
      <>
        <div className="vis-impact__state">
          <span>Potential impact · Variation #{liveLeaderId} vs. control</span>
          {leaderState && <span className="vis-impact__chip"><i style={{ background: leaderState.color }} />{leaderState.label}</span>}
        </div>
        <div className="vis-impact__grid">
          {dashCard(<IconCurrencyDollar size={16} stroke={2} />, 'Revenue Found', dash)}
          {dashCard(purchaseIconGuess() ? <IconShoppingCart size={16} stroke={2} /> : <IconTarget size={16} stroke={2} />, EXTRA_LABEL, dash)}
          {dashCard(<IconTrendingUp size={16} stroke={2} />, 'Conversion Lift', dash)}
        </div>
      </>
    );
  }

  /* Before a leader: the one-time intro, then the three cards. The Revenue card
     already runs its checks here (no data / calculating); the others wait. */
  function renderWaiting() {
    const purchaseIcon = purchaseIconGuess();
    let revenueBody: React.ReactNode = null;
    if (editing) {
      revenueBody = renderValueForm();
    } else if (serverLoaded && !goalValue.isPurchaseGoal && goalValue.state === 'none') {
      revenueBody = renderNoDataHero();
    } else if (serverLoaded && !goalValue.isPurchaseGoal && goalValue.state === 'collecting') {
      revenueBody = renderCollectingHero(goalValue.progress);
    }
    return (
      <>
        {!goalValue.introSeen && goalValue.loaded && (
          <div className="vis-impact__intro">
            <span>
              <b>New: Impact Summary.</b> See what your test is worth: extra conversions per month, the revenue
              behind them, and the lift. Numbers appear once Predictive Winner Detection names a leader.{' '}
              {goalValue.revenueTracked
                ? 'Revenue Found is calculated automatically from your sales data.'
                : 'Set up Visiopt campaigns to see Revenue Found.'}
            </span>
            <button className="vis-impact__cta" type="button" onClick={dismissIntro}>Got it</button>
          </div>
        )}
        <div className="vis-impact__state">
          <span>Waiting for Predictive Winner Detection to name a leader</span>
          <span className="vis-impact__chip"><i style={{ background: STATUS.neutral }} />No prediction yet</span>
        </div>
        <div className="vis-impact__grid">
          {dashCard(<IconCurrencyDollar size={16} stroke={2} />, 'Revenue Found', revenueBody)}
          {dashCard(purchaseIcon ? <IconShoppingCart size={16} stroke={2} /> : <IconTarget size={16} stroke={2} />, EXTRA_LABEL)}
          {dashCard(<IconTrendingUp size={16} stroke={2} />, 'Conversion Lift')}
        </div>
      </>
    );
  }

  function renderNumbers(n: ImpactNumbers) {
    const variation = `Variation #${n.leaderId}`;
    // Labelled POTENTIAL until a winner is set; locked results are the real impact.
    const potential = locked
      ? <span className="vis-impact__pot is-hidden" aria-hidden="true">POTENTIAL</span>
      : <span className="vis-impact__pot">POTENTIAL</span>;
    const potentialSpacer = <span className="vis-impact__pot is-hidden" aria-hidden="true">POTENTIAL</span>;
    /* The slow-sales note is about Visiopt's own sales numbers: it goes away
       whenever the customer gave the value (customer value, goal value or one sale). */
    const customerGaveValue = n.valueBasis === 'avg_sale' || n.valueBasis === 'manual';
    const liveSlow = !locked && serverLoaded
      && ((!n.isRevenueGoal && goalValue.slowNote && n.revenueFound !== null && !customerGaveValue)
        || (n.isRevenueGoal && goalValue.purchaseMode === 'measured_slow'));
    const lockedSlow = locked && lockedData && Number(lockedData.slow_note) === 1 && n.revenueFound !== null && n.revenueFound > 0
      && !customerGaveValue;

    const goalIcon = n.isRevenueGoal ? <IconShoppingCart size={16} stroke={2} /> : <IconTarget size={16} stroke={2} />;

    /* Step 8 — modeled purchase cards. The money comes from the earlier step's
       test result x the account's history, and every card says so. */
    const m = model;
    const stepLift = m && m.stepControlRate > 0 ? (m.stepLeaderRate - m.stepControlRate) / m.stepControlRate : null;
    const liftPct = Math.round((m ? stepLift || 0 : (n.lift as number)) * 100);

    return (
      <>
        <div className="vis-impact__state">
          <span>{locked ? 'Impact' : 'Potential impact'} · {variation} vs. control</span>
          {locked ? (
            <span className="vis-impact__chip"><i style={{ background: STATUS.good }} />Winner set{lockedDate ? ` · ${lockedDate}` : ''}</span>
          ) : (
            <span className="vis-impact__chip"><i style={{ background: leaderState?.color }} />{leaderState?.label}</span>
          )}
          {locked && <span>These results are saved and won't change.</span>}
        </div>
        <div className="vis-impact__grid">
          <div className="vis-impact__col">
            {potential}
            <span className="vis-impact__lbl"><IconCurrencyDollar size={16} stroke={2} /><span>Revenue Found</span></span>
            {m ? (!locked && editing ? renderValueForm() : (
              <>
                <span className="vis-impact__val is-hero">{formatMoneyCard(m.revenueFound)}<small>/mo</small></span>
                <span className="vis-impact__sub">
                  Modeled from your goal: {m.stepName} · {formatValue(m.valuePerStep)} per {m.stepName}{basisTag(m.valueBasis, '', true)}
                  {!locked ? (
                    <> · <button className="vis-impact__link" type="button" onClick={() => openValueForm(m.stepName)}>Edit</button></>
                  ) : null}
                </span>
                <span className="vis-impact__note">Annualized run rate {formatMoneyCard(m.revenueFound * 12)}</span>
              </>
            )) : renderHero(n)}
          </div>
          <div className="vis-impact__col">
            {potential}
            <span className="vis-impact__lbl" title={EXTRA_LABEL}>
              {/* Target for standard goals; the cart is reserved for purchase goals. */}
              {goalIcon}
              <span>{EXTRA_LABEL}</span>
            </span>
            {m ? (
              <>
                <span className="vis-impact__val">+{formatCount(m.purchasesFound)}<small>/mo</small></span>
                <span className="vis-impact__sub">Modeled from your goal: {m.stepName}</span>
                <span className="vis-impact__note">{formatPctPlain(m.closeRate)} of people who reach {m.stepName} go on to buy</span>
              </>
            ) : (
              <>
                <span className="vis-impact__val">+{formatCount(n.goalFound)}<small>/mo</small></span>
                <span className="vis-impact__sub">More {goalName} conversions a month {locked ? 'at traffic when winner was set' : 'at current traffic'}</span>
              </>
            )}
          </div>
          <div className="vis-impact__col">
            {potentialSpacer}
            <span className="vis-impact__lbl"><IconTrendingUp size={16} stroke={2} /><span>Conversion Lift</span></span>
            <span className="vis-impact__val">+{liftPct}%</span>
            <span className="vis-impact__sub">{variation} converts {liftPct}% better than the control{m ? ` on ${m.stepName}` : ''}</span>
            <span className="vis-impact__arrow">
              Conversion rate: {formatRate(m ? m.stepControlRate : n.controlRate)} <b>→</b> {formatRate(m ? m.stepLeaderRate : n.leaderRate)}
            </span>
          </div>
        </div>
        {liveSlow && (
          <div className="vis-impact__slownote">
            This test's sales seem to take longer than a few days, so revenue may read lower than it will end up.
            Visiopt campaigns track the full sales cycle for an accurate Revenue Found.
          </div>
        )}
        {lockedSlow && (
          <div className="vis-impact__slownote">
            The sales cycle is longer than this test, so revenue may end up higher.
          </div>
        )}
        {howOpen && renderHowPanel(n)}
      </>
    );
  }

  function renderHowPanel(n: ImpactNumbers) {
    const variation = `Variation #${n.leaderId}`;
    const vpc = n.valuePerConversion;
    const basis = !locked && serverLoaded ? goalValue.basis : null;
    const m = model;
    // The value's source in words, with what it is based on.
    let sourceLabel = basisLabel(n.valueBasis || '', n.valueSource) || 'your value';
    if (n.valueBasis === 'avg_sale' && n.avgSale && n.closeRate) {
      sourceLabel = `from your customer value: ${formatMoney(n.avgSale, 2)} × ${formatPctPlain(n.closeRate)} close rate`;
    }
    /* How long this goal's buyers take to buy (X) - not shown next to the
       customer's own value, which doesn't depend on it. */
    const timing = locked ? (lockedData && lockedData.timing ? lockedData.timing : null) : (serverLoaded ? goalValue.timing : null);
    const ownValue = (m ? m.valueBasis : n.valueBasis) === 'manual';
    const timingLine = timing && !ownValue ? (Number(timing.fallback) === 1
      ? <>Based on {goalName} buyers: fewer than 20 so far, so Visiopt uses 30 days.</>
      : <>Based on {goalName} buyers: 90% buy within {Number(timing.days)} days.</>) : null;
    const goalNote = <>Revenue Found is estimated from {m ? m.stepName : goalName}. Each goal gives its own estimate, because this variation may lift each step by a different amount. The goal closest to the sale gives the most reliable figure.</>;
    // While the test runs, a first-purchase value says how to switch to a customer value.
    const firstPurchaseLine = !locked && (m ? m.valueBasis : n.valueBasis) === 'first_purchase';
    // No value yet and no "Count as Purchase" webhook: how to mark the one that records sales.
    const salesWebhooks = !locked && serverLoaded && goalValue.state === 'none' ? goalValue.salesWebhooks : [];
    const purchaseHintLine = salesWebhooks.length === 0 && !locked && serverLoaded && goalValue.purchaseHint;

    /* Plain English first (B2): rates, extra conversions, money (green), context.
       Results are full numbers with "about" (only the cards abbreviate $1M+);
       the exact numbers live behind Show details. */
    const sentences: React.ReactNode[] = [];
    const calcRows: [string, string][] = [];
    const formulas: React.ReactNode[] = [];

    formulas.push(
      <>Monthly visitors = {formatCount(n.totalUniques)} ÷ {n.days} × 30.44 = <b>{formatCount(n.monthlyUniques)}</b></>
    );

    if (m) {
      /* Modeled purchase goal (step 8). */
      sentences.push(
        <>{variation} converts {formatRate(m.stepLeaderRate)} of visitors to {m.stepName}, compared with {formatRate(m.stepControlRate)} for the control.</>
      );
      sentences.push(
        <>At about {formatCount(n.monthlyUniques)} visitors a month, that's about {formatCount(m.stepFound)} more {m.stepName} conversions per month.</>
      );
      const stepValueNote = m.valueBasis === 'manual' && m.saleValue
        ? ` (your value: ${formatMoney(m.saleValue, 2)} per sale × ${formatPctPlain(m.closeRate)})`
        : m.valueBasis === 'first_purchase' ? basisTag('first_purchase', '', true) : '';
      sentences.push(
        <>Based on your history, {formatPctPlain(m.closeRate)} of people who reach {m.stepName} go on to buy ({goalName}), worth <Money n={m.valuePerStep} decimals={2} /> per {m.stepName}{stepValueNote}. Chosen because it has the most past buyers ({formatCount(m.customers)}). {m.stepFound.toFixed(2)} × <Money n={m.valuePerStep} decimals={2} /> = <AboutMoney n={m.revenueFound} /> per month.</>
      );
      sentences.push(
        <>Based on {n.days} {n.days === 1 ? 'day' : 'days'} of test data. Assumes {variation} is shown to all visitors and the extra {m.stepName} convert at the same rate as past ones.</>
      );
      if (timingLine) {
        sentences.push(timingLine);
      }
      if (firstPurchaseLine) {
        sentences.push(<>{FIRST_PURCHASE_NOTE_SALE}</>);
      }
      sentences.push(goalNote);
      calcRows.push(
        ['Leader', variation],
        ['Control', 'Variation #1'],
        [`${m.stepName} rate (leader)`, formatRate(m.stepLeaderRate)],
        [`${m.stepName} rate (control)`, formatRate(m.stepControlRate)],
        ['Monthly uniques', formatCount(n.monthlyUniques)],
        ['Test uniques', formatCount(n.totalUniques)],
        ['Days of data', String(n.days)],
        [`${m.stepName} found`, `+${m.stepFound.toFixed(2)}/mo`],
        ['Customers from history', `${formatCount(m.customers)} of ${formatCount(m.completions)}`],
        ['Close rate', formatRate(m.closeRate)],
        ['First purchase per customer', formatMoney(m.valuePerCustomer, 2)],
        ...(m.valueBasis === 'manual' && m.saleValue ? [['Your value per sale', formatMoney(m.saleValue, 2)] as [string, string]] : []),
        [`Value per ${m.stepName}`, formatMoney(m.valuePerStep, 2)],
        [`${goalName} found`, `+${m.purchasesFound.toFixed(2)}/mo`],
        ['Revenue found', formatMoney(m.revenueFound, 2)],
        ['Annualized revenue', formatMoney(m.revenueFound * 12, 2)],
      );
      formulas.push(
        <>{m.stepName} Found = ({formatRate(m.stepLeaderRate)} − {formatRate(m.stepControlRate)}) × {formatCount(n.monthlyUniques)} = <b>+{m.stepFound.toFixed(2)}/mo</b></>,
        <>Close rate = {formatCount(m.customers)} ÷ {formatCount(m.completions)} = <b>{formatRate(m.closeRate)}</b></>,
      );
      formulas.push(
        <>Value per {m.stepName} = {formatRate(m.closeRate)} × {formatMoney(m.valueBasis === 'manual' && m.saleValue ? m.saleValue : m.valuePerCustomer, 2)} = about <b>{formatMoney(m.valuePerStep, 2)}</b></>,
        <>{goalName} Found = {m.stepFound.toFixed(2)} × {formatRate(m.closeRate)} = about <b>+{m.purchasesFound.toFixed(2)}/mo</b></>,
        <>Revenue Found = {m.stepFound.toFixed(2)} × {formatMoney(m.valuePerStep, 2)} = about <b>{formatMoney(m.revenueFound)}/mo</b></>,
      );
    } else {
      sentences.push(
        <>{variation} converts {formatRate(n.leaderRate)} of visitors to {goalName}, compared with {formatRate(n.controlRate)} for the control.</>
      );
      sentences.push(
        <>At about {formatCount(n.monthlyUniques)} visitors a month, that's about {formatCount(n.goalFound)} more {goalName} conversions per month.</>
      );
      if (n.isRevenueGoal && n.revenueFound !== null && n.revenueFound > 0) {
        sentences.push(
          <>Visitors on {variation} bring in <Money n={n.leaderRevenuePerUnique} decimals={2} /> each, versus <Money n={n.controlRevenuePerUnique} decimals={2} /> on the control: <Money n={n.leaderRevenuePerUnique - n.controlRevenuePerUnique} decimals={2} /> more per visitor, or <AboutMoney n={n.revenueFound} /> more per month.</>
        );
      } else if (vpc && n.revenueFound !== null && n.revenueFound > 0) {
        sentences.push(
          <>Each {goalName} is worth {ownValue ? '' : 'about '}<Money n={vpc} decimals={2} /> ({sourceLabel}). {n.goalFound.toFixed(2)} × <Money n={vpc} decimals={2} /> = <AboutMoney n={n.revenueFound} /> per month.</>
        );
      }
      /* One combined context line: days of data + the value source's own story. */
      const assumes = <>Assumes {variation} is shown to all visitors and keeps performing at this rate.</>;
      const daysPart = `${n.days} ${n.days === 1 ? 'day' : 'days'} of test data`;
      // A goal after the sale (thank-you, upsell) is valued by the purchase just before it.
      const afterSale = !!goalValue.timing && goalValue.timing.kind === 'post';
      if (basis && !n.isRevenueGoal && vpc && n.valueSource !== 'manual') {
        const range = formatRange(basis.from, basis.to);
        // After the sale: how many of those conversions had a purchase just before them.
        const salesPart = afterSale
          ? `, ${formatCount(basis.customers)} of them with a purchase just before, in the same visit`
          : ' with the sales that followed';
        if (n.valueSource === 'campaign') {
          sentences.push(
            <>Based on {daysPart} and {formatCount(basis.booked)} {goalName} conversions ({range}) from your campaign data{afterSale ? salesPart : ''}. {assumes}</>
          );
        } else if (n.valueSource === 'past') {
          sentences.push(
            <>Based on {daysPart} and {formatCount(basis.booked)} {goalName} conversions from past tests ({range}){salesPart}. {assumes}</>
          );
        } else {
          sentences.push(
            <>Based on {daysPart} and {formatCount(basis.booked)} {goalName} conversions{salesPart}. {assumes}</>
          );
        }
      } else {
        sentences.push(
          <>Based on {daysPart}. {assumes}</>
        );
      }
      if (timingLine && (vpc || n.isRevenueGoal)) {
        sentences.push(timingLine);
      }
      if (firstPurchaseLine && vpc && !n.isRevenueGoal) {
        sentences.push(<>{FIRST_PURCHASE_NOTE}</>);
      }
      if (salesWebhooks.length > 0) {
        sentences.push(
          <>
            To show Revenue Found, Visiopt needs to know which webhook records your sales. Go to Settings → Payment Processing and Carts,
            edit {salesWebhooks.length === 1 ? `“${salesWebhooks[0].name}”` : 'your sales webhooks'}, and turn on “Count as Purchase”.
            {salesWebhooks.length > 1 ? ' If two webhooks report the same order, turn it on for only one.' : ''}
            {' '}Revenue Found will then be calculated from your real sales. Those sales also count as purchases in your other reports, such as New vs. Returning customers.
          </>
        );
      } else if (purchaseHintLine) {
        sentences.push(
          <>Turn on “Count as Purchase” so Impact Summary can measure actual sales. Its sales will also count as purchases in your other reports, such as New vs. Returning customers.</>
        );
      }
      sentences.push(goalNote);

      calcRows.push(
        ['Leader', variation],
        ['Control', 'Variation #1'],
        ['Leader uniques / conv', `${formatCount(n.leaderUniques)} / ${formatCount(n.leaderConversions)}`],
        ['Control uniques / conv', `${formatCount(n.controlUniques)} / ${formatCount(n.controlConversions)}`],
        ['Leader conv rate', formatRate(n.leaderRate)],
        ['Control conv rate', formatRate(n.controlRate)],
        ['Difference', `${((n.leaderRate - n.controlRate) * 100).toFixed(2)} pts`],
        ['Conversion lift', `+${((n.lift as number) * 100).toFixed(1)}%`],
        ['Test uniques', formatCount(n.totalUniques)],
        ['Days of data', String(n.days)],
        ['Monthly uniques', formatCount(n.monthlyUniques)],
        [`${goalName} found`, n.goalFound.toFixed(2)],
        [`Annualized ${goalName}`, `+${formatCount(n.goalFound * 12)}`],
      );
      formulas.push(
        <>{goalName} Found = ({formatRate(n.leaderRate)} − {formatRate(n.controlRate)}) × {formatCount(n.monthlyUniques)} = <b>+{formatCount(n.goalFound)}/mo</b></>
      );
      if (n.isRevenueGoal && n.revenueFound !== null) {
        calcRows.push(
          ['Revenue / visitor', `${formatMoney(n.leaderRevenuePerUnique, 2)} / ${formatMoney(n.controlRevenuePerUnique, 2)}`],
          ['Revenue found', formatMoney(n.revenueFound, 2)],
          ['Annualized revenue', formatMoney(n.revenueFound * 12, 2)],
        );
        formulas.push(
          <>Revenue Found = ({formatMoney(n.leaderRevenuePerUnique, 2)} − {formatMoney(n.controlRevenuePerUnique, 2)}) × {formatCount(n.monthlyUniques)} = about <b>{formatMoney(n.revenueFound)}/mo</b></>
        );
      } else if (vpc && n.revenueFound !== null) {
        if (basis && n.valueSource !== 'manual' && basis.booked > 0) {
          calcRows.push([afterSale ? 'Bought just before' : 'Customers (first purchase)', `${formatCount(basis.customers)} of ${formatCount(basis.booked)}`]);
        }
        calcRows.push(
          ['Value per conversion', `${formatMoney(vpc, 2)} (${sourceLabel})`],
          ['Revenue found', formatMoney(n.revenueFound, 2)],
          ['Annualized revenue', formatMoney(n.revenueFound * 12, 2)],
        );
        if (basis && n.valueSource !== 'manual' && basis.booked > 0) {
          if (n.valueBasis === 'avg_sale' && n.avgSale && n.closeRate) {
            formulas.push(
              <>Close rate = {formatCount(basis.customers)} ÷ {formatCount(basis.booked)} = <b>{formatRate(n.closeRate)}</b></>,
              <>Value per conversion = {formatMoney(n.avgSale, 2)} × {formatRate(n.closeRate)} = about <b>{formatMoney(vpc, 2)}</b></>,
            );
          } else {
            formulas.push(
              <>Value per conversion = {formatMoney(basis.revenue, 2)} in {afterSale ? 'purchases made just before' : 'first purchases'} ÷ {formatCount(basis.booked)} = <b>{formatMoney(vpc, 2)}</b></>
            );
          }
        }
        formulas.push(
          <>Revenue Found = {n.goalFound.toFixed(2)} × {formatMoney(vpc, 2)} = about <b>{formatMoney(n.revenueFound)}/mo</b></>
        );
      }
    }

    return (
      <div className="vis-impact__panel">
        <div className="vis-impact__plain">
          {sentences.map((s, i) => <span key={i}>{s}</span>)}
        </div>
        <button
          className="vis-impact__link vis-impact__details-toggle"
          type="button"
          aria-expanded={howDetails}
          onClick={() => setHowDetails((open) => !open)}
        >
          {howDetails ? 'Hide details' : 'Show details'}
        </button>
        {howDetails && (
          <>
            <div className="vis-impact__calc">
              {calcRows.map(([label, value]) => (
                <div key={label}><span>{label}</span><span>{value}</span></div>
              ))}
            </div>
            <div className="vis-impact__formula">
              {formulas.map((f, i) => <div key={i}>{f}</div>)}
            </div>
            <div className="vis-impact__pnote">
              FOUND counts only the extra results over the control.
            </div>
          </>
        )}
      </div>
    );
  }

  const forGoal = goalName ? ` for ${goalName}` : '';
  let body: React.ReactNode = null;
  if (!collapsed) {
    if (locked) {
      body = hasNumbers && impact
        ? renderNumbers(impact)
        : <div className="vis-impact__empty">No positive impact found{impact ? ` with Variation #${impact.leaderId}` : ''}{forGoal}.</div>;
    } else if (!singleGoal) {
      body = <div className="vis-impact__info">{'\u{2139}\u{FE0F}'} Impact Summary runs per goal. Select a specific conversion target above to see the impact.</div>;
    } else if (liveLeaderId === null) {
      body = renderWaiting();
    } else if (!serverReady) {
      body = renderLoading();
    } else if (!impact || !hasNumbers) {
      // "No positive impact" only when the control is ahead; an empty or too-thin date range says so instead.
      const controlAhead = noLift || (!!impact && impact.controlUniques > 0 && impact.leaderUniques > 0
        && (impact.leaderId === CONTROL_ID || impact.controlRate > impact.leaderRate));
      body = controlAhead
        ? <div className="vis-impact__empty">No positive impact found yet{forGoal}. The control is still ahead.</div>
        : <div className="vis-impact__info">{'\u{2139}\u{FE0F}'} Not enough traffic in this date range. Try a longer range.</div>;
    } else {
      body = renderNumbers(impact);
    }
  }

  return (
    <section className={`vis-impact${collapsed ? ' is-collapsed' : ''}`} style={vars} aria-label="Impact Summary">
      <div className="vis-impact__head">
        <span className="vis-impact__title"><IconChartBar size={16} stroke={2} />IMPACT SUMMARY <span className="vis-impact__beta">BETA</span></span>
        <span className="vis-impact__tools">
          {hasNumbers && !collapsed && (
            <button className="vis-impact__how" type="button" aria-expanded={howOpen} onClick={() => setHowOpen((open) => !open)}>
              <span className="vis-impact__q">?</span>How is this calculated?
            </button>
          )}
          <button
            className="vis-impact__chev"
            type="button"
            aria-label={collapsed ? 'Expand Impact Summary' : 'Collapse Impact Summary'}
            aria-expanded={!collapsed}
            onClick={toggleCollapsed}
          >
            <IconChevronUp size={14} stroke={2.4} />
          </button>
        </span>
      </div>
      {body}
    </section>
  );
}
